// CourseList.js
import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import BASE_URL from "../../config/config";
import Loader from "../common/Loader";
import ErrorMessage from "../common/ErrorMessage";
import CourseCard from "./CourseCard";
import "../../css/CourseList.css";

// آیکون‌های لازم برای نمایش وضعیت‌ها
import { FaChevronUp, FaChevronDown, FaDownload } from "react-icons/fa";

const CourseList = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [limit, setLimit] = useState(10);
  const [currentFilter, setCurrentFilter] = useState("all");
  const [displayedCourses, setDisplayedCourses] = useState(6);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [courseMedia, setCourseMedia] = useState({});

  const token = localStorage.getItem("token");
  const isAuthenticated = Boolean(token);

  // تابع getMediaFile
  const getMediaFile = useCallback(
    async (courseId, fileUrl, mediaType = "file") => {
      if (!token) {
        console.error("توکن احراز هویت یافت نشد!");
        return null;
      }

      if (!fileUrl) return null;

      if (courseMedia[courseId]?.mediaUrl) {
        return courseMedia[courseId].mediaUrl;
      }

      setCourseMedia((prev) => ({
        ...prev,
        [courseId]: { ...prev[courseId], loading: true, error: null },
      }));

      try {
        const response = await fetch(
          `${BASE_URL}/get-media?file_id=${encodeURIComponent(
            fileUrl
          )}&type=${mediaType}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            console.error("خطا: دسترسی غیرمجاز (توکن نامعتبر یا منقضی شده)");
          } else {
            const errorData = await response.json().catch(() => ({}));
            console.error(
              `خطا در دریافت فایل: ${response.status}`,
              errorData.error || ""
            );
            throw new Error(
              errorData.message || `خطا در دریافت فایل: ${response.status}`
            );
          }
          return null;
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setCourseMedia((prev) => ({
          ...prev,
          [courseId]: { ...prev[courseId], mediaUrl: url, loading: false },
        }));
        return url;
      } catch (error) {
        console.error("خطای کلی در دریافت فایل:", error);
        setCourseMedia((prev) => ({
          ...prev,
          [courseId]: {
            ...prev[courseId],
            error: error.message,
            loading: false,
          },
        }));
        return null;
      }
    },
    [token, courseMedia]
  );

  // --------- Fetch Courses ---------
  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      setError(null);

      try {
        const queryParams = new URLSearchParams({
          page: page,
          limit: limit,
          ...(searchQuery && { search: searchQuery }),
        });

        const response = await fetch(
          `${BASE_URL}/public/courses?${queryParams}`
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          const errorMessage =
            errorData?.message || `HTTP error: ${response.status}`;
          throw new Error(errorMessage);
        }

        const data = await response.json();
        setCourses(data.data.courses || []);
        setTotalPages(data.data.pagination?.totalPages || 1);
        setCourseMedia({});
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [page, searchQuery, limit]);

  // --------- Fetch Enrolled Courses (اگر کاربر لاگین است) ---------
  useEffect(() => {
    const fetchEnrollments = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await fetch(`${BASE_URL}/user/courses`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401) {
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch courses.");
        }

        const data = await response.json();
        setEnrolledCourses(data.data || []);
      } catch (err) {
        console.error("Error fetching enrolled courses:", err.message);
      }
    };
    fetchEnrollments();
  }, [isAuthenticated]);

  // اعمال فیلترها بر روی دوره‌ها
  const filteredCourses = courses.filter((course) => {
    // فیلتر جستجو
    if (searchQuery.trim() !== "") {
      const matchesSearch =
        course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.description.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;
    }

    // فیلتر سطح دوره
    if (currentFilter === "beginner" && course.level !== "beginner")
      return false;
    if (currentFilter === "intermediate" && course.level !== "intermediate")
      return false;
    if (currentFilter === "advanced" && course.level !== "advanced")
      return false;

    // فیلتر دوره‌های رایگان
    if (currentFilter === "free" && course.price !== "0") return false;

    return true;
  });

  // توابع کمکی برای تغییر صفحه و جستجو
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleFilterChange = (filter) => {
    setCurrentFilter(filter);
    setPage(1);
  };

  const loadMoreCourses = () => {
    setDisplayedCourses(displayedCourses + 6);
  };

  // تابع برای مدیریت دکمه‌های خرید/مشاهده
  const renderActionButton = (course) => {
    const enrollment = enrolledCourses.find(
      (enrolled) => enrolled.id === course.id
    );
    const userCourseStatus = enrollment?.status;

    if (!isAuthenticated) {
      return (
        <button
          onClick={() => {
            alert("لطفاً ابتدا وارد شوید.");
            navigate("/login");
          }}
          className="btn-view-course"
        >
          خرید دوره
        </button>
      );
    }

    if (userCourseStatus === "completed") {
      return (
        <Link to={`/courses/${course.id}`}>
          <button className="btn-view-course">مشاهده دوره</button>
        </Link>
      );
    }

    if (userCourseStatus === "pending") {
      return (
        <span className="course-status-pending">در انتظار تکمیل سفارش...</span>
      );
    }

    return (
      <Link
        to={{
          pathname: `/dashboard/payment/${course.id}`,
          state: {
            courseTitle: course.title,
            coursePrice: course.price,
          },
        }}
      >
        <button className="btn-view-course">خرید دوره</button>
      </Link>
    );
  };

  // تابع برای لود کردن مدیا و نمایش آن
  const handleMediaInteraction = async (courseId, fileUrl, fileType) => {
    if (courseMedia[courseId]?.mediaUrl && !courseMedia[courseId]?.error)
      return;

    if (courseMedia[courseId]?.loading) return;

    await getMediaFile(courseId, fileUrl, fileType);
  };

  // رندر کردن مدیا
  const renderCourseMedia = (course) => {
    const mediaInfo = courseMedia[course.id];
    const isVideo = course.intro_video?.endsWith(".mp4");
    const isAudio = course.intro_video?.endsWith(".mp3");
    const isDocument = !isVideo && !isAudio && course.intro_video;

    if (course.intro_video && mediaInfo?.mediaUrl) {
      if (isVideo) {
        return (
          <video
            controls
            width="100%"
            className="session-video"
            preload="metadata"
            src={mediaInfo.mediaUrl}
            key={course.id}
          >
            <source src={mediaInfo.mediaUrl} type="video/mp4" />
            مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند.
          </video>
        );
      } else if (isAudio) {
        return (
          <audio
            controls
            className="session-audio"
            preload="metadata"
            src={mediaInfo.mediaUrl}
            key={course.id}
          >
            <source src={mediaInfo.mediaUrl} type="audio/mpeg" />
            مرورگر شما از پخش صوت پشتیبانی نمی‌کند.
          </audio>
        );
      } else if (isDocument && mediaInfo?.mediaUrl) {
        return (
          <a
            href={mediaInfo.mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="session-download"
          >
            <FaDownload /> دانلود فایل: {course.intro_video.split("/").pop()}
          </a>
        );
      }
    }

    if (mediaInfo?.loading) {
      return <div className="session-loading">در حال بارگیری فایل...</div>;
    }

    if (mediaInfo?.error) {
      return <div className="session-error">{mediaInfo.error}</div>;
    }

    if (course.intro_video && !mediaInfo?.mediaUrl) {
      return (
        <button
          onClick={() =>
            handleMediaInteraction(
              course.id,
              course.intro_video,
              course.intro_video_type || "file"
            )
          }
          className="media-interaction-button"
        >
          {isVideo
            ? "پخش ویدیوی معرفی"
            : isAudio
            ? "پخش فایل صوتی"
            : "دانلود فایل"}
        </button>
      );
    }

    return null;
  };

  const goToCourseDetail = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  // محاسبه قیمت نهایی با تخفیف
  const calculateFinalPrice = (price, discount) => {    
    const originalPrice = parseFloat(price) || 0;
    const discountPercent = parseFloat(discount) || 0;
    return originalPrice - (originalPrice * discountPercent) / 100;
  };

  return (
    <div>
      {/* بخش فیلترها و نوار جستجو */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          {/* Search and Limit Bar */}
          <div class="mb-6">
            <div class="max-w-md mx-auto">
              <div class="relative">
                <input
                  type="text"
                  id="courseSearchInput"
                  class="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-full focus:border-primary focus:outline-none transition-all bg-white text-right"
                  placeholder="جستجو در دوره‌ها..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <svg
                  class="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  ></path>
                </svg>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              className={`filter-button ${
                currentFilter === "all" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("all")}
            >
              همه دوره‌ها
            </button>
            <button
              className={`filter-button ${
                currentFilter === "beginner" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("beginner")}
            >
              مقدماتی
            </button>
            <button
              className={`filter-button ${
                currentFilter === "intermediate" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("intermediate")}
            >
              متوسط
            </button>
            <button
              className={`filter-button ${
                currentFilter === "advanced" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("advanced")}
            >
              پیشرفته
            </button>
            <button
              className={`filter-button ${
                currentFilter === "free" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("free")}
            >
              رایگان
            </button>
          </div>
        </div>
      </section>

      {loading && <Loader />}
      {error && <ErrorMessage message={error} />}

      {/* بخش لیست دوره‌ها */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          {!loading && !error && filteredCourses.length === 0 && (
            <p className="no-courses text-center py-8">دوره‌ای یافت نشد.</p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCourses.slice(0, displayedCourses).map((course) => (
              <div key={course.id} className="course-card-container">
                <CourseCard
                  course={{
                    id: course.id,
                    title: course.title,
                    description: course.description,
                    level: course.level,
                    levelName:
                      course.level === "beginner"
                        ? "مقدماتی"
                        : course.level === "intermediate"
                        ? "متوسط"
                        : "پیشرفته",
                    price: course.price === "0" ? "رایگان" : course.price,
                    discount: course.discount || 0,
                    isFree: course.is_free || course.price === "0",
                    finalPrice:
                      parseFloat(course.discount) > 0
                        ? calculateFinalPrice(course.price, course.discount)
                        : null,
                    duration: course.duration || "نامشخص",
                    students: course.enrollment_count || "0",
                    rating: course.rating || "0",
                    icon:  course.cover_pic || "📚" , // می‌توانید آیکون مناسب را بر اساس نوع دوره تنظیم کنید
                    isFree: course.price === "0",
                  }}
                  onClick={() => goToCourseDetail(course.id)}
                />

                {/* نمایش مدیا در کارت دوره */}
                {/* {course.intro_video && (
                  <div className="course-intro-media mt-4">
                    {renderCourseMedia(course)}
                  </div>
                )} */}

                {/* دکمه اقدام */}
                {/* <div className="mt-4">
                  {renderActionButton(course)}
                </div> */}
              </div>
            ))}
          </div>

          {/* دکمه بارگذاری بیشتر */}
          {displayedCourses < filteredCourses.length && (
            <div className="text-center mt-12">
              <button
                className="btn-primary px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg"
                onClick={loadMoreCourses}
              >
                📚 مشاهده دوره‌های بیشتر
              </button>
            </div>
          )}

          {/* Pagination Controls */}
          {!loading && !error && totalPages > 1 && (
            <div className="pagination mt-12 flex justify-center">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (pageNumber) => (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`pagination-button mx-1 px-4 py-2 rounded ${
                      pageNumber === page ? "active" : ""
                    }`}
                    disabled={pageNumber === page}
                  >
                    {pageNumber}
                  </button>
                )
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CourseList;
